import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const chapters = [
  {
    n: "I",
    title: "Weddings",
    body:
      "From the first pinned bloom to the last taper, we build ceremonies that hold a room — brass arches, hand-tied peonies, the exact right light.",
    tag: "Ceremony · Reception · Mehndi",
  },
  {
    n: "II",
    title: "Engagements",
    body:
      "Small dinners, private terraces, the promise made under a garden of candles. We keep the moment intimate — never small.",
    tag: "Proposal · Save-the-date · First dinners",
  },
  {
    n: "III",
    title: "Corporate",
    body:
      "Product launches, galas, keynote nights. The room speaks the brand before a single slide loads. Precision, discretion, taste.",
    tag: "Galas · Launches · Board dinners",
  },
  {
    n: "IV",
    title: "Private Parties",
    body:
      "Birthdays that arrive like a novel — a first chapter of arrival, a middle of laughter, a last of quiet at a candlelit table.",
    tag: "Milestones · Anniversaries · At-home",
  },
  {
    n: "V",
    title: "Custom Décor",
    body:
      "One-off installations, fabricated in-studio. Suspended florals, brass sculpture, chandeliers restored by hand. Nothing off-the-shelf.",
    tag: "Bespoke · Fabrication · Restoration",
  },
];

export default function ManifestoChapters() {
  return (
    <section id="chapters" className="relative bg-[#4A0E1F] px-6 py-24 sm:px-10 md:px-14 md:py-32">
      <div className="mx-auto max-w-6xl">
        <div className="mb-16 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="mb-4 flex items-center gap-3">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="font-body text-[11px] uppercase tracking-[0.36em] text-[#D4AF37]">
                Chapters · The Manifesto
              </span>
            </div>
            <h3 className="font-serif text-[9vw] leading-[0.95] tracking-tight text-[#FDFBF7] sm:text-6xl md:text-7xl">
              Five ways
              <span className="italic text-[#D4AF37]"> we compose </span>
              a room.
            </h3>
          </div>
          <p className="max-w-sm font-body text-[15px] leading-[1.7] text-[#FDFBF7]/70">
            No packages. No copy-paste. Every event is a piece — written on paper,
            fabricated in the studio, staged by hand.
          </p>
        </div>

        <div className="divide-y divide-[#D4AF37]/25 border-y border-[#D4AF37]/25">
          {chapters.map((c, i) => (
            <ChapterRow key={c.n} chapter={c} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ChapterRow({ chapter, idx }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [30, -30]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.3, 1, 1, 0.4]);

  return (
    <motion.div
      ref={ref}
      style={{ opacity }}
      className="relative grid grid-cols-1 items-start gap-8 py-12 md:grid-cols-[240px_1fr_auto] md:gap-16 md:py-16"
    >
      <motion.div style={{ y }} className="flex items-baseline gap-6">
        <div className="chapter-num">{chapter.n}</div>
      </motion.div>
      <div>
        <div className="mb-4 font-body text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]/80">
          Chapter · {String(idx + 1).padStart(2, "0")}
        </div>
        <h4 className="mb-4 font-serif text-4xl leading-[1.05] tracking-tight text-[#FDFBF7] md:text-5xl">
          {chapter.title}
        </h4>
        <p className="max-w-lg font-body text-[15px] leading-[1.75] text-[#FDFBF7]/80">
          {chapter.body}
        </p>
      </div>
      <div className="max-w-[200px] font-italic-serif text-[15px] leading-[1.6] text-[#D4AF37]">
        {chapter.tag}
      </div>
    </motion.div>
  );
}
